import { motion } from 'framer-motion'
import { duration, ease } from '../lib/motion'
import { trackClickButton as trackClickButtonTikTok } from '../lib/tiktokPixel'
import { trackClickButton as trackClickButtonLinkedIn } from '../lib/linkedInInsight'

export default function Button({
  children,
  href,
  onClick,
  variant = 'primary',
  className = '',
  type = 'button',
  disabled = false,
  target,
  rel,
  // Forces a real save-to-device instead of opening in the browser's
  // built-in viewer — needed for anything meant to be downloaded (e.g.
  // the free guide PDF), since a plain <a href> to a PDF just previews it
  // in most browsers. Pass a filename string, or `true` to keep the
  // original filename.
  download,
  // Opt-in, not automatic for every variant="primary" button — see
  // tiktokPixel.js's trackClickButton doc comment for why (the inquiry
  // form's submit button is also primary-styled, and that click already
  // fires a Contact event on success, so it deliberately doesn't set this).
  ttqTrack = false,
}) {
  const base =
    'inline-flex items-center justify-center gap-2 rounded-xl px-6 py-3 text-sm font-medium whitespace-nowrap focus-visible:outline-none'

  const style =
    variant === 'primary'
      ? { background: 'var(--accent)', color: 'var(--accent-text)' }
      : {
          background: 'transparent',
          color: 'var(--text-primary)',
          border: '1px solid var(--border-strong)',
        }

  const Comp = href ? motion.a : motion.button

  const handleClick = (e) => {
    if (disabled) return
    if (ttqTrack) {
      trackClickButtonTikTok({
        label: typeof children === 'string' ? children : 'CTA',
        href,
      })
      // Same button instances (Hero, Navbar, FinalCTA, CampaignHero,
      // InsightArticle) also count as a LinkedIn conversion signal — see
      // linkedInInsight.js for why this needs a manual call and how to
      // activate it. Safely no-ops until conversion IDs are configured.
      trackClickButtonLinkedIn()
    }
    onClick?.(e)
  }

  return (
    <Comp
      href={disabled ? undefined : href}
      onClick={handleClick}
      type={href ? undefined : type}
      disabled={href ? undefined : disabled}
      aria-disabled={disabled || undefined}
      target={href ? target : undefined}
      rel={href ? rel : undefined}
      download={href ? download : undefined}
      className={`${base} ${className}`}
      style={{ ...style, ...(disabled ? { opacity: 0.6, pointerEvents: 'none' } : {}) }}
      whileHover={{ y: -1, filter: 'brightness(1.08)' }}
      whileTap={{ scale: 0.98 }}
      transition={{ duration: duration.fast, ease: ease.out }}
    >
      {children}
    </Comp>
  )
}
